import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';

const badgeVariants = cva(
	'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold font-heading transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
	{
		variants: {
			variant: {
				default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
				secondary:
					'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
				destructive:
					'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
				outline: 'text-foreground',
				// success/warning — añadidos documentando Screens/Movimientos B2B: el
				// código real (AdminContribucionesV2.tsx, STATUS_CONFIG) repite este
				// mismo par de clases sueltas (bg-primary/10 + amber-500/10) en cada
				// sitio que pinta un estado en vez de pasar por Badge — aquí se
				// incorporan como variantes reales del primitive en lugar de seguir
				// copiando className a mano por pantalla.
				// El texto va en `--primary-text` y el fondo y el borde siguen en `--primary`:
				// como relleno translúcido cumple, y como texto no. Medido sobre el propio
				// `bg-primary/10`, que es su fondo real y no la tarjeta: 2,82:1 antes, 6,60:1
				// ahora.
				success: 'border-primary/20 bg-primary/10 text-primary-text hover:bg-primary/20',
				// Esta variante llevaba el color del tema claro a pelo y el del oscuro
				// en tokens: `bg-amber-500/10 text-amber-800` + `dark:bg-warning-surface
				// dark:text-warning`. Ahora los dos temas se nombran igual, porque
				// --warning-surface y --warning-text ya tienen valor en :root Y en
				// .dark (ver tokens.css).
				//
				// Lo que había que cuidar al cambiarlo era no perder el contraste, que
				// aquí venía de un bug real cazado con axe: amber-600 daba 2,95:1 y
				// hasta amber-700 se quedaba en 4,38:1 sobre el fondo REAL del tema
				// (medido con getComputedStyle, no supuesto — no es blanco puro).
				// amber-800 pasaba con 6,19:1, y --warning-text sobre
				// --warning-surface da 6,29:1. El oscuro no se mueve: sus valores son
				// exactamente los que escribían esos `dark:`.
				//
				// El `hover` se queda en `/80` del propio token, que es lo que ya hacía
				// en oscuro. En claro antes oscurecía y ahora aclara: es un matiz de un
				// estado transitorio y no justifica un token más.
				warning:
					'border-warning-border bg-warning-surface text-warning-text hover:bg-warning-surface/80'
			}
		},
		defaultVariants: {
			variant: 'default'
		}
	}
);

export interface BadgeProps
	extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
	return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
